import React from 'react';
import { Box, IconButton, Skeleton, Stack, Typography } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { EntityImage } from '../../components/EntityImage';
import { themeConstants } from '../../theme';

const HEADER_IMAGE_SIZE = 48;
export const EXPLORER_HEADER_HEIGHT = 88;

const HEADER_SX = {
    mt: { xs: 0, md: -2 },
    mb: 2.5,
    minHeight: EXPLORER_HEADER_HEIGHT,
    overflow: 'visible',
    borderBottom: 1,
    borderColor: 'divider',
    boxSizing: 'border-box',
} as const;

const HEADER_IMAGE_SX = {
    borderRadius: themeConstants.border.radius,
    overflow: 'hidden',
    '& .MuiAvatar-img': { objectFit: 'cover' },
} as const;

export type ExplorerHeaderImage = {
    name: string;
    src?: string | null;
};

type ExplorerHeaderProps = {
    title: string;
    subtitle?: string | null;
    /** Sits on the same line as the subtitle, to its right. */
    subtitleExtra?: React.ReactNode;
    /** Rounded-square entity image. Omit on list pages. */
    image?: ExplorerHeaderImage;
    /** Omit to hide the back button. */
    onBack?: () => void;
    /** Handles, format links, etc. Rendered under the title/subtitle. */
    extra?: React.ReactNode;
    loading?: boolean;
    /** Trailing filters: dates, export, view tabs, category. */
    children?: React.ReactNode;
};

export function ExplorerHeader({
    title,
    subtitle,
    subtitleExtra,
    image,
    onBack,
    extra,
    loading = false,
    children,
}: ExplorerHeaderProps) {
    const showSubtitle = subtitle != null && subtitle !== '';
    const hasActions = children != null && children !== false;

    return (
        <Stack
            direction="row"
            justifyContent="space-between"
            alignItems={onBack ? 'center' : 'stretch'}
            spacing={2}
            sx={{
                ...HEADER_SX,
                ...(onBack ? { py: 1.5 } : { height: EXPLORER_HEADER_HEIGHT }),
            }}
        >
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ minWidth: 0, flex: 1, overflow: 'visible' }}
            >
                {onBack ? (
                    loading ? (
                        <Skeleton variant="circular" width={40} height={40} />
                    ) : (
                        <IconButton onClick={onBack}>
                            <ArrowBackIcon />
                        </IconButton>
                    )
                ) : null}
                {image ? (
                    loading ? (
                        <Skeleton
                            variant="rounded"
                            width={HEADER_IMAGE_SIZE}
                            height={HEADER_IMAGE_SIZE}
                            sx={{ borderRadius: 1 }}
                        />
                    ) : (
                        <EntityImage
                            name={image.name}
                            src={image.src}
                            size={HEADER_IMAGE_SIZE}
                            variant="bordered"
                            sx={HEADER_IMAGE_SX}
                        />
                    )
                ) : null}
                <Box sx={{ minWidth: 0, overflow: 'visible' }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={200} height={40} />
                            {subtitle != null ? <Skeleton variant="text" width={150} height={20} /> : null}
                        </>
                    ) : (
                        <>
                            <Typography
                                variant="h1"
                                component="h1"
                                sx={{
                                    overflow: 'visible',
                                    lineHeight: 1,
                                    ...(onBack ? { fontSize: '2rem' } : null),
                                }}
                            >
                                {title}
                            </Typography>
                            {showSubtitle || subtitleExtra ? (
                                <Stack
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="baseline"
                                    sx={{ mt: 0.25, minWidth: 0 }}
                                >
                                    {showSubtitle ? (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                        >
                                            {subtitle}
                                        </Typography>
                                    ) : null}
                                    {subtitleExtra}
                                </Stack>
                            ) : null}
                            {extra ? (
                                <Box
                                    sx={{
                                        mt: 0.25,
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {extra}
                                </Box>
                            ) : null}
                        </>
                    )}
                </Box>
            </Stack>

            {hasActions ? (
                loading ? (
                    <Skeleton variant="rounded" width={220} height={40} />
                ) : (
                    <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        sx={{
                            flexShrink: 0,
                            flexWrap: 'nowrap',
                            ...(onBack
                                ? { alignSelf: 'center' }
                                : { alignSelf: 'stretch', height: '100%' }),
                        }}
                    >
                        {children}
                    </Stack>
                )
            ) : null}
        </Stack>
    );
}

export default ExplorerHeader;
